import React, {useEffect, useState} from "react";
import {connect, useSelector} from "react-redux";
import {RouteComponentProps} from "react-router";
import {css, Theme} from "@emotion/react";

import {Hidden, WideContent} from "@pg-design/grid-module";
import {m, mb, mt} from "@pg-design/helpers-css";
import {FaqSection} from "@pg-mono/faq";
import {parseSearch} from "@pg-mono/request";
import {RequestState} from "@pg-mono/request-state";

import {IRPStore} from "../../app/rp_reducer";
import {LeadModal} from "../../application/components/LeadModal";
import {DesktopBlend} from "../../atoms/DesktopBlend";
import {LocationLinks} from "../../atoms/LocationLinks";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {IFaqStore} from "../../faq/reducers/faq_reducer";
import {MobileListingBottomBar} from "../../navigation/components/MobileListingBottomBar";
import {OfferListLocationLinksWrapper} from "../../offer/atoms/OfferLocationLinksWrapper";
import {InvestingOfferListViewNavigation} from "../../offer/components/InvestingOfferListViewNavigation";
import {useDsaRatingSortExperiment} from "../../offer/hooks/use_dsa_rating_sort_experiment";
import {OfferListSeoDescription} from "../../offer/list/components/OfferListSeoDescription";
import {OfferListSort} from "../../offer/list/components/OfferListSort";
import {OfferListSortMobile} from "../../offer/list/components/OfferListsSortMobile";
import {SearchSortString} from "../../offer/list/constants/SearchSort";
import {OfferType} from "../../offer/types/OfferType";
import {IOfferCardAddressContext} from "../../offer/utils/get_offer_card_address";
import {Country} from "../../region/types/Country";
import {forInvestmentOfferListSearchHit} from "../../tracking/algolytics_hits/for_investment_offer_list_search_hit";
import {ISearchRegion, prepareOfferListSearchData} from "../../tracking/algolytics_hits/offer_list_search_hit";
import {normalizeOfferListIntoSearchPayload} from "../../tracking/algolytics_hits/utils/normalize_offer_data_to_payload";
import {InvestmentListingStickyHeader} from "../atoms/InvestmentListingStickyHeader";
import {InvestmentOfferHeader} from "../atoms/InvestmentOfferHeader";
import {investmentOfferCities, InvestmentOfferTypeSlug} from "../constants/investment_offer_constants";
import {useInvestmentOfferFilters} from "../hooks/use_investment_offer_filters";
import {useInvestmentOfferViewsUrl} from "../hooks/use_investment_offer_views_url";
import {IParsedInvestmentOfferCategories} from "../list/actions/parse_investment_offer_url";
import {InvestmentOfferListListSection} from "../list/components/InvestmentOfferListListSection";
import {InvestmentOfferListMobileFilters} from "../list/components/InvestmentOfferListMobileFilters";
import {InvestmentOfferListPagination} from "../list/components/InvestmentOfferListPagination";
import {IInvestmentOffersOffersStore} from "../list/reducers/investment_offer_list_reducer";
import {IInvestmentOfferSeoContentStore} from "../list/reducers/investment_offer_seo_content_reducer";

interface IStateProps {
    categories: IParsedInvestmentOfferCategories;
    offers: IInvestmentOffersOffersStore;
    pageTitle: string;
    propertiesCount: number | null;
    seo: IInvestmentOfferSeoContentStore;
    faq: IFaqStore;
}

interface IOwnProps extends RouteComponentProps<{}> {}

interface IProps extends IStateProps, IOwnProps {}

const InvestmentOfferListViewC: React.FC<IProps> = (props) => {
    const sort = useSelector((state: IRPStore) => state.investmentOffer.list.offers.latestQuery.sort) as SearchSortString;
    const voivodeshipRegions = useSelector((state: IRPStore) => state.investmentOffer.abroadVoivodeshipRegions);
    const country = Number(props.offers.latestQuery.country);
    const regionTypes = useSelector((state: IRPStore) => (state.variables.data ? state.variables.data.region_type : []));

    const [isMobileFiltersActive, setMobileFiltersActive] = useState(false);
    const [isMobileSortActive, setMobileSortActive] = useState(false);
    const [isInvestmentOffersBlendActive, setInvestmentOffersBlendActive] = useState(false);

    const [windowHeight, setWindowHeight] = useState(0);

    useDsaRatingSortExperiment();

    const {mapUrl} = useInvestmentOfferViewsUrl();
    const {formValues: filtersFormValues, onChange: onFiltersChange, onSave: onFiltersSave, onStoreFormValues, resetFilters} = useInvestmentOfferFilters();
    const query = parseSearch(props.location.search);
    const currentPage = typeof query.page === "string" ? parseInt(query.page, 10) : 1;
    const type = typeof query.type === "string" ? parseInt(query.type, 10) : undefined;
    const selectedCity = investmentOfferCities.find((city) => city.slug === props.categories.subcategory);
    const rawSearchDistance = props.offers.latestQuery.distance;
    const addressContext: IOfferCardAddressContext = {
        searchedRegion: selectedCity,
        searchDistance: Array.isArray(rawSearchDistance) ? rawSearchDistance[0] : rawSearchDistance
    };
    // analytics hits on search change
    useEffect(() => {
        const offerPayload = normalizeOfferListIntoSearchPayload(props.offers.offerList);
        const searchRegions: ISearchRegion[] = voivodeshipRegions.map((region) => {
            const regionType = region && regionTypes?.find((t) => t.value === region.type);
            return {short_name: region.name, type: region.type, type__display: regionType?.label.toLocaleLowerCase() || ""};
        });
        const searchPayload = prepareOfferListSearchData({
            offers_count: props.offers.count,
            properties_count: props.propertiesCount,
            latestQuery: props.offers.latestQuery,
            searchRegions,
            query
        });

        if (props.offers.requestState === RequestState.Success) {
            forInvestmentOfferListSearchHit(props.categories.category, props.categories.subcategory, offerPayload, searchPayload);
        }
    }, [props.categories.category, props.categories.subcategory, props.offers.requestState]);

    useEffect(() => {
        setWindowHeight(document.body.clientHeight);
    }, [isInvestmentOffersBlendActive]);

    useEffect(() => {
        onStoreFormValues(isMobileFiltersActive ? filtersFormValues : null);
    }, [isMobileFiltersActive]);
    return (
        <article css={mb(4)}>
            <DesktopBlend isActive={isInvestmentOffersBlendActive} pageHeight={`${windowHeight}px`} top="11.2rem" heightDecrease="18rem" />

            <InvestingOfferListViewNavigation setInvestmentOffersBlendActive={setInvestmentOffersBlendActive} />
            {props.categories.category === InvestmentOfferTypeSlug.ABROAD && (
                <InvestmentListingStickyHeader latestQuery={props.offers.latestQuery} setInvestmentOffersBlendActive={setInvestmentOffersBlendActive} />
            )}
            <WideContent>
                <InvestmentOfferHeader
                    categories={props.categories}
                    css={m(1.5, 0, 6, 0)}
                    pageTitle={props.pageTitle}
                    linkUrl={mapUrl}
                    propertiesCount={props.propertiesCount}
                    offerCount={props.offers.count}
                    region={props.categories.subcategory}
                    offerType={type}
                    page={props.offers.page}
                />
            </WideContent>

            <WideContent>
                <Hidden on="mobile">
                    <OfferListSort name="offer-sort" latestQuerySort={sort} />
                </Hidden>
            </WideContent>

            <InvestmentOfferListListSection
                addressContext={addressContext}
                offerList={props.offers.offerList}
                latestQuery={props.offers.latestQuery}
                isLoading={props.offers.requestState === RequestState.Waiting}
            />

            <WideContent>
                <InvestmentOfferListPagination />

                {props.offers.page === 1 && props.seo?.seoContent?.seo_description && props.seo?.requestState === RequestState.Success && (
                    <OfferListSeoDescription css={mt(13)} seo_description={props.seo.seoContent.seo_description} />
                )}
            </WideContent>

            <MobileOptionsModal modalHeading="Filtruj" isOpen={isMobileFiltersActive} onModalClose={() => setMobileFiltersActive(false)}>
                <InvestmentOfferListMobileFilters
                    onChange={(newFiltersValue) => onFiltersChange(newFiltersValue, false)}
                    onSave={() => {
                        onFiltersSave();
                        setMobileFiltersActive(false);
                    }}
                    onReset={resetFilters}
                    values={filtersFormValues}
                />
            </MobileOptionsModal>

            <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={() => setMobileSortActive(false)}>
                <OfferListSortMobile latestQuerySort={sort} name="sort" onSubmit={() => setMobileSortActive(false)} onMobileResetForm={resetFilters} />
            </MobileOptionsModal>

            {country === Country.SPAIN && voivodeshipRegions && (
                <OfferListLocationLinksWrapper css={locationLinksWrapper}>
                    <LocationLinks recommendedRegions={voivodeshipRegions} offerType={OfferType.FLAT} country={Country.SPAIN} abroadVoivodeshipRegions />
                    <LocationLinks recommendedRegions={voivodeshipRegions} offerType={OfferType.HOUSE} country={Country.SPAIN} abroadVoivodeshipRegions />
                </OfferListLocationLinksWrapper>
            )}

            {currentPage === 1 && <FaqSection faqContent={props.faq.data} />}

            <LeadModal />
            <MobileListingBottomBar isMap={false} mapUrl={mapUrl} />
        </article>
    );
};

export const InvestmentOfferListView = connect(mapStateToProps)(InvestmentOfferListViewC);

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        offers: state.investmentOffer.list.offers,
        propertiesCount: state.investmentOffer.list.propertiesCount,
        categories: state.investmentOffer.categories,
        seo: state.investmentOffer.list.seo,
        pageTitle: state.metaData.standardMetaData.title,
        faq: state.faq
    };
}
const locationLinksWrapper = (theme: Theme) => css`
    margin: 0 0 3.5rem 0;

    @media (min-width: ${theme.breakpoints.md}) {
        margin: 0 0 4.2rem 0;
    }
`;
import React, {useEffect, useState} from "react";
import {connect, useSelector} from "react-redux";
import {RouteComponentProps} from "react-router";
import {css, Theme} from "@emotion/react";

import {Hidden, WideContent} from "@pg-design/grid-module";
import {m, mb, mt} from "@pg-design/helpers-css";
import {FaqSection} from "@pg-mono/faq";
import {parseSearch} from "@pg-mono/request";
import {RequestState} from "@pg-mono/request-state";

import {IRPStore} from "../../app/rp_reducer";
import {LeadModal} from "../../application/components/LeadModal";
import {DesktopBlend} from "../../atoms/DesktopBlend";
import {LocationLinks} from "../../atoms/LocationLinks";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {IFaqStore} from "../../faq/reducers/faq_reducer";
import {MobileListingBottomBar} from "../../navigation/components/MobileListingBottomBar";
import {OfferListLocationLinksWrapper} from "../../offer/atoms/OfferLocationLinksWrapper";
import {InvestingOfferListViewNavigation} from "../../offer/components/InvestingOfferListViewNavigation";
import {useDsaRatingSortExperiment} from "../../offer/hooks/use_dsa_rating_sort_experiment";
import {OfferListSeoDescription} from "../../offer/list/components/OfferListSeoDescription";
import {OfferListSort} from "../../offer/list/components/OfferListSort";
import {OfferListSortMobile} from "../../offer/list/components/OfferListsSortMobile";
import {SearchSortString} from "../../offer/list/constants/SearchSort";
import {OfferType} from "../../offer/types/OfferType";
import {IOfferCardAddressContext} from "../../offer/utils/get_offer_card_address";
import {Country} from "../../region/types/Country";
import {forInvestmentOfferListSearchHit} from "../../tracking/algolytics_hits/for_investment_offer_list_search_hit";
import {ISearchRegion, prepareOfferListSearchData} from "../../tracking/algolytics_hits/offer_list_search_hit";
import {normalizeOfferListIntoSearchPayload} from "../../tracking/algolytics_hits/utils/normalize_offer_data_to_payload";
import {InvestmentListingStickyHeader} from "../atoms/InvestmentListingStickyHeader";
import {InvestmentOfferHeader} from "../atoms/InvestmentOfferHeader";
import {investmentOfferCities, InvestmentOfferTypeSlug} from "../constants/investment_offer_constants";
import {useInvestmentOfferFilters} from "../hooks/use_investment_offer_filters";
import {useInvestmentOfferViewsUrl} from "../hooks/use_investment_offer_views_url";
import {IParsedInvestmentOfferCategories} from "../list/actions/parse_investment_offer_url";
import {InvestmentOfferListListSection} from "../list/components/InvestmentOfferListListSection";
import {InvestmentOfferListMobileFilters} from "../list/components/InvestmentOfferListMobileFilters";
import {InvestmentOfferListPagination} from "../list/components/InvestmentOfferListPagination";
import {IInvestmentOffersOffersStore} from "../list/reducers/investment_offer_list_reducer";
import {IInvestmentOfferSeoContentStore} from "../list/reducers/investment_offer_seo_content_reducer";

interface IStateProps {
    categories: IParsedInvestmentOfferCategories;
    offers: IInvestmentOffersOffersStore;
    pageTitle: string;
    propertiesCount: number | null;
    seo: IInvestmentOfferSeoContentStore;
    faq: IFaqStore;
}

interface IOwnProps extends RouteComponentProps<{}> {}

interface IProps extends IStateProps, IOwnProps {}

const InvestmentOfferListViewC: React.FC<IProps> = (props) => {
    const sort = useSelector((state: IRPStore) => state.investmentOffer.list.offers.latestQuery.sort) as SearchSortString;
    const voivodeshipRegions = useSelector((state: IRPStore) => state.investmentOffer.abroadVoivodeshipRegions);
    const country = Number(props.offers.latestQuery.country);
    const regionTypes = useSelector((state: IRPStore) => (state.variables.data ? state.variables.data.region_type : []));

    const [isMobileFiltersActive, setMobileFiltersActive] = useState(false);
    const [isMobileSortActive, setMobileSortActive] = useState(false);
    const [isInvestmentOffersBlendActive, setInvestmentOffersBlendActive] = useState(false);

    const [windowHeight, setWindowHeight] = useState(0);

    useDsaRatingSortExperiment();

    const {mapUrl} = useInvestmentOfferViewsUrl();
    const {formValues: filtersFormValues, onChange: onFiltersChange, onSave: onFiltersSave, onStoreFormValues, resetFilters} = useInvestmentOfferFilters();
    const query = parseSearch(props.location.search);
    const currentPage = typeof query.page === "string" ? parseInt(query.page, 10) : 1;
    const type = typeof query.type === "string" ? parseInt(query.type, 10) : undefined;
    const selectedCity = investmentOfferCities.find((city) => city.slug === props.categories.subcategory);
    const rawSearchDistance = props.offers.latestQuery.distance;
    const addressContext: IOfferCardAddressContext = {
        searchedRegion: selectedCity,
        searchDistance: Array.isArray(rawSearchDistance) ? rawSearchDistance[0] : rawSearchDistance
    };
    // analytics hits on search change
    useEffect(() => {
        const offerPayload = normalizeOfferListIntoSearchPayload(props.offers.offerList);
        const searchRegions: ISearchRegion[] = voivodeshipRegions.map((region) => {
            const regionType = region && regionTypes?.find((t) => t.value === region.type);
            return {short_name: region.name, type: region.type, type__display: regionType?.label.toLocaleLowerCase() || ""};
        });
        const searchPayload = prepareOfferListSearchData({
            offers_count: props.offers.count,
            properties_count: props.propertiesCount,
            latestQuery: props.offers.latestQuery,
            searchRegions,
            query
        });

        if (props.offers.requestState === RequestState.Success) {
            forInvestmentOfferListSearchHit(props.categories.category, props.categories.subcategory, offerPayload, searchPayload);
        }
    }, [props.categories.category, props.categories.subcategory, props.offers.requestState]);

    useEffect(() => {
        setWindowHeight(document.body.clientHeight);
    }, [isInvestmentOffersBlendActive]);

    useEffect(() => {
        onStoreFormValues(isMobileFiltersActive ? filtersFormValues : null);
    }, [isMobileFiltersActive]);
    return (
        <article css={mb(4)}>
            <DesktopBlend isActive={isInvestmentOffersBlendActive} pageHeight={`${windowHeight}px`} top="11.2rem" heightDecrease="18rem" />

            <InvestingOfferListViewNavigation setInvestmentOffersBlendActive={setInvestmentOffersBlendActive} />
            {props.categories.category === InvestmentOfferTypeSlug.ABROAD && (
                <InvestmentListingStickyHeader latestQuery={props.offers.latestQuery} setInvestmentOffersBlendActive={setInvestmentOffersBlendActive} />
            )}
            <WideContent>
                <InvestmentOfferHeader
                    categories={props.categories}
                    css={m(1.5, 0, 6, 0)}
                    pageTitle={props.pageTitle}
                    linkUrl={mapUrl}
                    propertiesCount={props.propertiesCount}
                    offerCount={props.offers.count}
                    region={props.categories.subcategory}
                    offerType={type}
                    page={props.offers.page}
                />
            </WideContent>

            <WideContent>
                <Hidden on="mobile">
                    <OfferListSort name="offer-sort" latestQuerySort={sort} />
                </Hidden>
            </WideContent>

            <InvestmentOfferListListSection
                addressContext={addressContext}
                offerList={props.offers.offerList}
                latestQuery={props.offers.latestQuery}
                isLoading={props.offers.requestState === RequestState.Waiting}
            />

            <WideContent>
                <InvestmentOfferListPagination />

                {props.offers.page === 1 && props.seo?.seoContent?.seo_description && props.seo?.requestState === RequestState.Success && (
                    <OfferListSeoDescription css={mt(13)} seo_description={props.seo.seoContent.seo_description} />
                )}
            </WideContent>

            <MobileOptionsModal modalHeading="Filtruj" isOpen={isMobileFiltersActive} onModalClose={() => setMobileFiltersActive(false)}>
                <InvestmentOfferListMobileFilters
                    onChange={(newFiltersValue) => onFiltersChange(newFiltersValue, false)}
                    onSave={() => {
                        onFiltersSave();
                        setMobileFiltersActive(false);
                    }}
                    onReset={resetFilters}
                    values={filtersFormValues}
                />
            </MobileOptionsModal>

            <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={() => setMobileSortActive(false)}>
                <OfferListSortMobile latestQuerySort={sort} name="sort" onSubmit={() => setMobileSortActive(false)} onMobileResetForm={resetFilters} />
            </MobileOptionsModal>

            {country === Country.SPAIN && voivodeshipRegions && (
                <OfferListLocationLinksWrapper css={locationLinksWrapper}>
                    <LocationLinks recommendedRegions={voivodeshipRegions} offerType={OfferType.FLAT} country={Country.SPAIN} abroadVoivodeshipRegions />
                    <LocationLinks recommendedRegions={voivodeshipRegions} offerType={OfferType.HOUSE} country={Country.SPAIN} abroadVoivodeshipRegions />
                </OfferListLocationLinksWrapper>
            )}

            {currentPage === 1 && <FaqSection faqContent={props.faq.data} />}

            <LeadModal />
            <MobileListingBottomBar isMap={false} mapUrl={mapUrl} />
        </article>
    );
};

export const InvestmentOfferListView = connect(mapStateToProps)(InvestmentOfferListViewC);

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        offers: state.investmentOffer.list.offers,
        propertiesCount: state.investmentOffer.list.propertiesCount,
        categories: state.investmentOffer.categories,
        seo: state.investmentOffer.list.seo,
        pageTitle: state.metaData.standardMetaData.title,
        faq: state.faq
    };
}
const locationLinksWrapper = (theme: Theme) => css`
    margin: 0 0 3.5rem 0;

    @media (min-width: ${theme.breakpoints.md}) {
        margin: 0 0 4.2rem 0;
    }
`;
